/**
 * OBJEÇÃO EM DOIS NÍVEIS — o que travou a venda (fonte única dos rótulos).
 *
 * NÃO confundir com LOSS_REASONS (constants/lossReasons.js). São listas
 * deliberadamente diferentes: objeção é oportunidade VIVA — o lead travou, mas ainda
 * está conversando; motivo de perda é o negócio morto. Misturar as duas faria a
 * métrica de perda contar lead em andamento. Decisão do Gustavo, 2026-08-04.
 *
 * ⚠️ REESCRITA EM 2026-08-07. A lista de um nível virou uma de dois, e o motivo
 * apareceu no primeiro relatório: "preço" respondia por 72% das objeções, e uma fatia
 * de 72% não sustenta decisão nenhuma. Preço são QUATRO objeções, e cada uma pede uma
 * ação diferente:
 *
 *   achou_caro             → argumento de valor
 *   tem_mais_barato        → margem, ou desistir cedo
 *   sem_verba_agora        → NÃO é preço alto, é orçamento estourado. Linha de
 *                            entrada, parcelamento, retomar em 60 dias
 *   esperava_preco_fabrica → NÃO é preço, é expectativa de canal. Comunicação de
 *                            entrada, não margem
 *
 * Slugs espelham CwCustom::Crm::Leitor::Taxonomia — mudar aqui exige mudar lá (o
 * backend valida a lista fechada e recusa slug desconhecido, nos dois níveis).
 *
 * pt-BR locked (v1): rótulos hardcoded, como no resto do CRM. Sem i18n.
 */

/** Nível 1 — o que o vendedor escolhe primeiro, e o que agrupa o relatório. */
export const OBJECOES = [
  { value: 'preco', label: 'Preço' },
  { value: 'sumiu', label: 'Sumiu' },
  { value: 'prazo', label: 'Prazo' },
  { value: 'produto', label: 'Produto' },
  { value: 'logistica', label: 'Logística' },
  { value: 'quantidade', label: 'Quantidade' },
  { value: 'concorrente', label: 'Concorrente' },
  { value: 'momento', label: 'Momento' },
];

/**
 * Nível 2 — o detalhe, dentro do nível 1 escolhido. É opcional: melhor sem detalhe
 * que com o detalhe errado. Um detalhe pendurado no pai errado corrompe as duas
 * fatias de uma vez, e o backend recusa o par inconsistente.
 */
export const OBJECAO_DETALHES = {
  preco: [
    { value: 'achou_caro', label: 'Achou caro' },
    { value: 'tem_mais_barato', label: 'Achou mais barato em outro lugar' },
    {
      value: 'sem_verba_agora',
      label: 'Sem verba agora (orçamento estourado)',
    },
    { value: 'esperava_preco_fabrica', label: 'Esperava preço de fábrica' },
  ],
  sumiu: [
    { value: 'nunca_respondeu', label: 'Nunca respondeu' },
    { value: 'sumiu_no_meio', label: 'Sumiu no meio da conversa' },
    { value: 'sumiu_apos_orcamento', label: 'Sumiu depois do orçamento' },
  ],
  prazo: [
    { value: 'producao_longa', label: 'Produção demora demais' },
    { value: 'precisa_urgente', label: 'Precisa para ontem' },
    { value: 'sem_data_definida', label: 'Ainda não tem data' },
  ],
  produto: [
    { value: 'medida_nao_atende', label: 'A medida não atende' },
    { value: 'modelo_nao_atende', label: 'O modelo não atende' },
    { value: 'nao_temos_item', label: 'Não temos o item' },
  ],
  logistica: [
    { value: 'frete_caro', label: 'Frete caro' },
    { value: 'frete_demora', label: 'Frete demora' },
    { value: 'nao_entrega_regiao', label: 'Não entregamos na região' },
  ],
  quantidade: [
    { value: 'minimo_alto', label: 'Pedido mínimo alto' },
    { value: 'quer_unidade', label: 'Quer comprar uma unidade' },
  ],
  concorrente: [
    { value: 'fechou_com_outro', label: 'Fechou com outro' },
    { value: 'ja_tem_fornecedor', label: 'Já tem fornecedor' },
  ],
  momento: [
    { value: 'ainda_planejando', label: 'Ainda planejando' },
    { value: 'loja_nao_abriu', label: 'A loja ainda não abriu' },
    { value: 'so_pesquisando', label: 'Só pesquisando preço' },
  ],
};

/**
 * Rótulos da lista ANTIGA (até 2026-08-07). Não estão mais no seletor, mas milhares
 * de cards históricos guardam esses slugs — sem este mapa a linha do tempo e o painel
 * mostrariam o slug cru, ou nada. Sai da tela, não do banco.
 */
const OBJECOES_LEGADAS = {
  pensar: 'Vai pensar / falar com alguém',
  comparar: 'Quer comparar com concorrente',
  prazo_entrega: 'Prazo de entrega',
  frete_distancia: 'Frete / distância',
  forma_pagamento: 'Forma de pagamento',
  outro: 'Outro',
};

/** Rótulo de exibição de um slug de nível 1 já gravado (painel, linha do tempo). */
export const objecaoLabel = value =>
  OBJECOES.find(o => o.value === value)?.label ?? OBJECOES_LEGADAS[value] ?? '';

/**
 * Rótulo do detalhe (nível 2). Procura em toda a taxonomia porque o slug de nível 2 é
 * único — quem exibe nem sempre tem o nível 1 à mão.
 *
 * Cai para string vazia em detalhe que não é slug: no legado, `objecao_detalhe` era a
 * descrição em TEXTO LIVRE do 'outro'. Quem exibe deve mostrar o texto cru nesse caso.
 */
export const objecaoDetalheLabel = value =>
  Object.values(OBJECAO_DETALHES)
    .flat()
    .find(d => d.value === value)?.label ?? '';
